import { gql } from '@apollo/client'
import { Stack, Theme, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSendEmailMutation } from '~/common/generated/apollo-operations'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Button } from '~/components/ui/atoms/button/Button'
import { Modal } from '../ui/molecules/modal/Modal'
import { TextField } from '../ui/molecules/text-field/TextField'
import { useDossSnackbar } from './DossSnackbar'
gql`
  mutation sendEmail($input: SendEmailInput!) {
    sendEmail(input: $input)
  }
`
function getStyles(theme: Theme) {
  return {
    root: {
      '& .MuiDialog-paper': {
        maxWidth: 800,
      },
      content: {
        display: 'flex',
        flexDirection: 'column',
      },
      title: {
        marginBottom: theme.spacing(2),
      },
      row: {
        display: 'flex',
        marginBottom: theme.spacing(2),
        label: {
          width: '120px',
        },
        field: {
          width: '100%',
          flexGrow: 1,
          '& .MuiInputBase-input': {
            paddingBlock: theme.spacing(1),
            paddingInline: theme.spacing(1.5),
          },
          '& .MuiInputBase-multiline': {
            padding: 0,
          },
        },
        message: {
          width: '100%',
          flexGrow: 1,
          '& .MuiInputBase-input': {
            minHeight: '100px',
            paddingBlock: theme.spacing(1),
            paddingInline: theme.spacing(1.5),
          },
          '& .MuiInputBase-multiline': {
            padding: 0,
          },
        },
      },
    },
    actions: {
      width: '100%',
    },
  }
}

interface SendEmailDialogProps {
  open: boolean
  onClose: () => void
  title: string
  defaultTo?: string
  defaultSubject?: string
  defaultBody?: string
  emailBodyAdmin?: string
}

/**
 * Dialog to send an email
 */
export function SendEmailDialog({
  open,
  onClose,
  title,
  defaultTo,
  defaultSubject,
  defaultBody,
  emailBodyAdmin,
}: React.PropsWithChildren<SendEmailDialogProps>) {
  const styles = useStyles(getStyles)
  const [sendEmail] = useSendEmailMutation()
  const snackbar = useDossSnackbar()
  const { t } = useTypedTranslation()
  const [to, setTo] = useState(defaultTo ?? '')
  const [subject, setSubject] = useState(defaultSubject ?? '')
  const [userBody, setUserBody] = useState(defaultBody ?? '')

  useEffect(() => {
    if (open) {
      setTo(defaultTo ?? '')
      setSubject(defaultSubject ?? '')
      setUserBody(defaultBody ?? '')
    }
  }, [open, defaultTo, defaultSubject, defaultBody])

  const handleClose = () => {
    onClose()
    setTo('')
    setSubject('')
    setUserBody('')
  }

  const handleSubmit = async () => {
    const body = emailBodyAdmin ? `${userBody}\n\n${emailBodyAdmin}` : userBody
    await sendEmail({
      variables: {
        input: {
          to,
          subject,
          body,
        },
      },
    }).then(() => {
      snackbar.showSuccess(t('send_email_dialog.email_sent_success'))
    })
    handleClose()
  }

  const disableSendButton = !to || !subject || !userBody

  return (
    <Modal
      actions={
        <Button
          disabled={disableSendButton}
          onClick={handleSubmit}
          size="small"
          variant="secondary"
        >
          {t('send_email_dialog.send')}
        </Button>
      }
      fullWidth
      onClose={handleClose}
      open={open}
      stopPropagation={false}
      title={title}
    >
      <Stack direction="column" spacing={1} sx={styles.root.row}>
        <Typography sx={styles.root.row.label} variant="label">
          {t('send_email_dialog.subject')}
        </Typography>
        <TextField
          error={!subject}
          fullWidth
          helperText={!subject ? t('send_email_dialog.subject_required') : null}
          name="subject"
          onChange={(event) => setSubject(event.currentTarget.value)}
          sx={styles.root.row.field}
          value={subject}
        />
      </Stack>
      <Stack direction="column" spacing={1} sx={styles.root.row}>
        <Typography sx={styles.root.row.label} variant="label">
          {t('send_email_dialog.message')}
        </Typography>
        <TextField
          error={!userBody}
          fullWidth
          helperText={!userBody ? t('send_email_dialog.body_required') : null}
          maxRows={100}
          multiline
          name="body"
          onChange={(event) => setUserBody(event.currentTarget.value)}
          sx={styles.root.row.message}
          value={userBody}
        />
      </Stack>
    </Modal>
  )
}
